import { AGENTIC_LOGIC_SURFACE_BASE_CAP_MS } from "../runtime/budget";
import type { AgenticLogicBudgetBasis } from "../../../dtos/agentic-logic/publish";
import {
  AGENTIC_LOGIC_SURFACE_LABEL,
  type AgenticLogicSurface,
  AgenticLogicSurfaceList,
} from "../../../dtos/agentic-logic/surface";

/**
 * The evaluation budget a rule must fit, given the surfaces its enabled
 * placements render on: the tightest cap among them.
 *
 * An UNASSIGNED rule (empty `surfaces`) gets the tightest cap overall, not the
 * loosest. The publish job freezes a rule's budget at publish time and the
 * gate never re-runs on assignment, so gating an unassigned rule loosely lets
 * it be assigned to checkout afterwards and time out at serve time. The
 * tightest cap cannot be invalidated by any later assignment, which is what
 * makes the gate's promise true: this rule fits anywhere it can be placed.
 */
export function agenticLogicTightestSurfaceBudgetMs(
  surfaces: readonly AgenticLogicSurface[]
): number {
  if (surfaces.length === 0) {
    return Math.min(...Object.values(AGENTIC_LOGIC_SURFACE_BASE_CAP_MS));
  }
  return Math.min(
    ...surfaces.map((surface) => AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[surface])
  );
}

/**
 * The surface whose cap decides the budget for a set: the argmin of
 * {@link AGENTIC_LOGIC_SURFACE_BASE_CAP_MS} over it, with an empty set reading
 * as "every surface it could be placed on" — exactly the reading
 * {@link agenticLogicTightestSurfaceBudgetMs} takes, so the surface a sentence
 * names and the number it quotes cannot disagree.
 */
function tightestSurface(
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicSurface {
  const candidates = surfaces.length > 0 ? surfaces : AgenticLogicSurfaceList;
  return candidates.reduce((tightest, surface) =>
    AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[surface] <
    AGENTIC_LOGIC_SURFACE_BASE_CAP_MS[tightest]
      ? surface
      : tightest
  );
}

/** Merchant-facing names, in the surfaces' own wire order. */
function labelSurfaces(surfaces: readonly AgenticLogicSurface[]): string {
  return surfaces
    .map((surface) => AGENTIC_LOGIC_SURFACE_LABEL[surface])
    .join(", ");
}

/**
 * The ONE rendering of "where this rule's evaluation budget came from",
 * composed here so the gate `result` line, the gate detail renderer in the
 * workspace and any future consumer say the same thing.
 *
 * Written because the budget-bearing gates used to publish the threshold as a
 * bare number, and the workspace — the only surface that has to explain it —
 * called it "the assigned budget" for rules assigned nowhere. The word
 * "assigned" appears in the output IF AND ONLY IF `basis` is `"assigned"`.
 *
 * `basis: undefined` is the legacy job row: a manifest frozen before the field
 * existed genuinely cannot say which world it was in, so the sentence makes NO
 * claim rather than inventing the likeliest one.
 *
 * The surface the sentence names is DERIVED from `surfaces` here rather than
 * passed in, so it cannot disagree with
 * {@link agenticLogicTightestSurfaceBudgetMs}.
 */
export function renderAgenticLogicBudgetBasis(input: {
  budgetMs: number;
  surfaces: readonly AgenticLogicSurface[];
  basis: AgenticLogicBudgetBasis | undefined;
}): string {
  const { budgetMs, surfaces, basis } = input;
  if (basis === undefined) {
    return `Held to a ${budgetMs}ms evaluation budget.`;
  }
  const cap = `${budgetMs}ms ${AGENTIC_LOGIC_SURFACE_LABEL[tightestSurface(surfaces)]} cap`;
  if (basis === "any_surface") {
    return (
      `Held to the ${cap} — the tightest cap on any surface, because this ` +
      `rule declares no surfaces yet.`
    );
  }
  if (basis === "declared") {
    return (
      `Held to the ${cap}, the tightest of the surfaces this rule declares ` +
      `(${labelSurfaces(surfaces)}). The rule is not assigned to a placement ` +
      `yet, so it is gated at the tightest cap it could later be placed on.`
    );
  }
  return surfaces.length > 1
    ? `Held to the ${cap}, the tightest of the surfaces this rule is assigned to (${labelSurfaces(surfaces)}).`
    : `Held to the ${cap} — the surface this rule is assigned to.`;
}
